import { useQuery } from "@tanstack/react-query";
import { useOrderContext } from "app/providers/OrderProvider";
import { useEffect, useState } from "react";
import { api } from "shared/api";
import { AvailableBumbalDate } from "shared/api/middleware-api-client";
import { getCurrentDate, getFinalDate } from "shared/utils/date";

export const useAvailableBumbalDates = () => {
  const { order, type } = useOrderContext();

  const [fetchAmount, setFetchAmount] = useState<number>(0);
  const [pageMonth, setPageMonth] = useState<number>(new Date().getMonth() + 1);
  const [pageYear, setPageYear] = useState<number>(new Date().getFullYear());
  const [bumbalDates, setBumbalDates] = useState<AvailableBumbalDate[]>([]);
  const [hasAnyResult, setHasAnyResult] = useState<boolean>(false);

  // reset when order/type change
  useEffect(() => {
    setBumbalDates([]);
    setFetchAmount(0);
    setHasAnyResult(false);
    setPageMonth(new Date().getMonth() + 1);
    setPageYear(new Date().getFullYear());
  }, [order?.id, type]);

  const enabled = Boolean(order?.id && type && pageMonth && pageYear);

  const { isLoading, isFetching, data, refetch } = useQuery({
    queryKey: [
      "gettingAvailableBumbalDates",
      order?.id,
      type,
      pageMonth,
      pageYear,
    ],

    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await api().gersAvailableBumbalDates(
        order!.id,
        type,
        getCurrentDate(pageMonth, pageYear),
        getFinalDate(pageMonth, pageYear),
      );
      return (res.data ?? []) as AvailableBumbalDate[];
    },
    onSuccess: (result) => {
      // attempts bookkeeping
      if (result.length === 0) {
        // only auto-advance while we have no results yet and <5 attempts
        setFetchAmount((n) => {
          const next = hasAnyResult ? n : n + 1;
          return next;
        });

        if (!hasAnyResult) {
          // advance month (state change will trigger query via queryKey)
          setPageMonth((m) => {
            const nextMonth = m + 1 > 12 ? 1 : m + 1;
            if (m + 1 > 12) setPageYear((y) => y + 1);
            return nextMonth;
          });
        }
        return;
      }

      // got data for this window
      setHasAnyResult(true);
      setFetchAmount(0);

      // merge immutably and dedupe by dateFrom
      setBumbalDates((prev) => {
        const map = new Map<string, AvailableBumbalDate>();
        for (const d of prev) map.set(d.dateFrom, d);
        for (const d of result) map.set(d.dateFrom, d);
        return Array.from(map.values()).sort((a, b) =>
          a.dateFrom.localeCompare(b.dateFrom),
        );
      });
    },
    enabled: enabled && fetchAmount < 6,
  });

  // maintain original names for consumers
  const isBumbalDataLoading = isLoading && !hasAnyResult; // first load only
  const isBumbalDataFetching = isFetching; // also true on refetches
  const bumbalData = data ?? []; // kept for API parity

  return {
    isBumbalDataFetching,
    isBumbalDataLoading,
    bumbalData,
    bumbalDates,
    fetchAmount,
    pageMonth,
    pageYear,
    setPageMonth,
    setPageYear,
    refetchBumbalDates: refetch,
  };
};
